import { User, UserRole, UserPermissions, getShiftsPermissionLevel } from '../types';

// Permission checker utility
export const hasPermission = (user: User | null, permission: keyof UserPermissions): boolean => {
  if (!user) return false;

  // Super admin has access to everything
  if (user.role === UserRole.SUPER_ADMIN) return true;

  // Agency admin has access to everything within their agency, EXCEPT agencies management
  if (user.role === UserRole.AGENCY_ADMIN) {
    return permission !== 'agencies'; // billing is always accessible to agency admins
  }

  // For staff members, check specific permissions
  if (user.role === UserRole.STAFF) {
    // If user has no permissions defined, they have no access (except dashboard)
    if (!user.permissions) {
      return permission === 'dashboard';
    }

    // Special handling for shifts permission (supports levels)
    if (permission === 'shifts') {
      const level = getShiftsPermissionLevel(user.permissions.shifts);
      return level !== 'none';
    }

    // Check specific permission
    return user.permissions[permission] === true;
  }

  // For other roles, default to false
  return false;
};

// Check if user has any of the specified permissions
export const hasAnyPermission = (user: User | null, permissions: Array<keyof UserPermissions>): boolean => {
  if (!user) return false;
  
  // Super admin has access to everything
  if (user.role === UserRole.SUPER_ADMIN) return true;
  
  return permissions.some(permission => hasPermission(user, permission));
};

// Check if user has all of the specified permissions
export const hasAllPermissions = (user: User | null, permissions: Array<keyof UserPermissions>): boolean => {
  if (!user) return false;
  
  // Super admin has access to everything
  if (user.role === UserRole.SUPER_ADMIN) return true;
  
  return permissions.every(permission => hasPermission(user, permission));
};

// Get list of permissions the user has
export const getUserPermissions = (user: User | null): Array<keyof UserPermissions> => {
  if (!user) return [];
  
  // Super admin has all permissions
  if (user.role === UserRole.SUPER_ADMIN) {
    return ['dashboard', 'patients', 'caregivers', 'matching', 'shifts', 'invoices', 'emails', 'templates', 'tasks', 'knowledge', 'files', 'staff', 'settings', 'billing', 'agencies'];
  }
  
  if (!user.permissions) return ['dashboard']; // Default minimal permission
  
  return Object.entries(user.permissions)
    .filter(([_, hasAccess]) => hasAccess)
    .map(([permission, _]) => permission as keyof UserPermissions);
};

// Check if user can access a specific route/page
export const canAccessRoute = (user: User | null, routePath: string): boolean => {
  if (!user) return false;
  
  // Super admin can access everything
  if (user.role === UserRole.SUPER_ADMIN) return true;
  
  // Map routes to required permissions
  const routePermissions: Record<string, keyof UserPermissions> = {
    '/dashboard': 'dashboard',
    '/patients': 'patients',
    '/caregivers': 'caregivers',
    '/matching': 'matching',
    '/shifts': 'shifts',
    '/invoices': 'invoices',
    '/emails': 'emails',
    '/templates': 'templates',
    '/tasks': 'tasks',
    '/files': 'files',
    '/staff': 'staff',
    '/dgkp': 'staff', // DGKP-Verwaltung gehört zur Mitarbeiterverwaltung
    '/settings': 'settings',
    '/agencies': 'agencies',
    '/agency-menu-config': 'agencies', // Super admin only feature
    '/system-settings': 'settings', // Super admin only feature
  };
  
  // Find the permission required for this route. Sub-routes inherit it:
  // /patients/<id>/overview needs 'patients' like /patients itself — an exact
  // lookup let staff without the Klienten permission open any client
  // overview via the Betreuer page or the dashboard.
  const matchedRoute = Object.keys(routePermissions).find(
    (route) => routePath === route || routePath.startsWith(`${route}/`),
  );
  const requiredPermission = matchedRoute ? routePermissions[matchedRoute] : undefined;
  if (!requiredPermission) {
    return true; // Allow access to unknown routes (they may have their own protection)
  }
  
  return hasPermission(user, requiredPermission);
}; 